// Commons
import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  BLC_STATUS_TYPE,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  OptionType,
} from "react-lib/apps/HISV3/common/CommonInterface";
import getPdfMake from "react-lib/appcon/common/pdfMake";
import getHtmlToPdfmake from "react-lib/appcon/common/getHtmlToPdfmake";
import { PDFDocument } from "pdf-lib";
import { ITDocumentDefinitions } from "../pdfForm/CheckupReportCommon";

// Utils
import { printURL, base64ToUint8Array } from "react-lib/utils/printURL";

/*================================ APIs ==============================*/
import EncounterList from "issara-sdk/apis/EncounterList_core";
import CheckupReportList from "issara-sdk/apis/CheckupReportList_core";
import CheckupReportCoverExaminationPrint from "issara-sdk/apis/CheckupReportCoverExaminationPrint_core";
import CheckupReportLabPrint from "issara-sdk/apis/CheckupReportLabPrint_core";
import CheckupReportXrayPrint from "issara-sdk/apis/CheckupReportXrayPrint_core";
import DoctorRecommendationTemplateList from "issara-sdk/apis/DoctorRecommendationTemplateList_core";
import DoctorRecommendationTemplateDetail from "issara-sdk/apis/DoctorRecommendationTemplateDetail_core";
import CheckupReportDetail from "issara-sdk/apis/CheckupReportDetail_core";
import PackageOrderEncounterMapList from "issara-sdk/apis/PackageOrderEncounterMapList_apps_APP";
import PatientDetailViewByHn from "issara-sdk/apis/PatientDetailViewByHn_apps_REG";

/*================================ Serializers ==============================*/
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import CheckupReportSerializer from "issara-sdk/types/CheckupReportSerializer_core";
import DoctorRecommendationTemplateSerializer from "issara-sdk/types/DoctorRecommendationTemplateSerializer_core";

/*================================ PDFS ==============================*/
import FormCheckupReport from "../pdfForm/FormCheckupReport";
import FormDoctorRecommendationReport, {
  CheckupReportDoctorNote,
} from "../pdfForm/FormDoctorRecommendationReport";
import {
  CheckupReportCover,
  CheckupReportLab,
  CheckupReportXray,
} from "../pdfForm/CheckupReportType";

/*================================ Type-Defining Constants ==============================*/

export const ACTIONS = {
  FETCH: "FETCH",
  FETCH_PDF: "FETCH_PDF",
  FETCH_DOCTOR_NOTE: "FETCH_DOCTOR_NOTE",
  FETCH_ALL_PDF: "FETCH_ALL_PDF",
  GET_PACKAGE_LIST: "GET_PACKAGE_LIST",
  GET_CHECKUP_LIST: "GET_CHECKUP_LIST",
  GET_CHECKUP_REPORT: "GET_CHECKUP_REPORT",
  GET_TEMPLATE: "GET_TEMPLATE",
  CREATE_TEMPLATE: "CREATE_TEMPLATE",
  EDIT_TEMPLATE: "EDIT_TEMPLATE",
  DELETE_TEMPLATE: "DELETE_TEMPLATE",
  PRINT_REPORT: "PRINT_REPORT",
  SEND_MOBILE: "SEND_MOBILE",
  SAVE_REPORT: "SAVE_REPORT",
  EDIT: "EDIT",
} as const;

export const CARD_NAME = "HealthRecommendation" as const;

/*================================ Types and Interface ==============================*/

export type State = Partial<{
  HealthRecommendationSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    // * --- Common Options
    commonOptions: CommonOptions;
    // * --- Data
    data: HealthRecommendationData;
    pdfData: PdfData;
  }> | null;
}>;

export type Language = "th" | "en";

export type CommonOptions = Partial<{
  encounterList: EncounterSerializer[];
  checkupReportList: CheckupReportSerializer[];
  doctorNoteTemplateList: DoctorRecommendationTemplateSerializer[];
}>;

export type HealthRecommendationData = Partial<{
  language: Language;
  encounter: number;
  // package: number;
  // doctor_note: string;
  // checkup_report: number;
  checkup_report_data: CheckupReportSerializer;
}>;

export type PdfData = Partial<{
  checkup_report: string;
  approve_report: string;
}>;

export type ReportParams = Partial<{
  checkupReportId: number;
  language: Language;
}>;

type PackageOrderEncounterMapSerializer = Record<string, PackageOrderList>;

type PackageOrderList = { package_orders: PackageOrderItem[] };

type PackageOrderItem = {
  package_id: number;
  package_name: string;
  package_code: string;
  package_item_approved: boolean;
  package_approved: boolean;
  approved_by: string | null;
};

export type ActionReport = "SAVE" | "APPROVE" | "UNAPPROVE";

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "masterOptions"
  | "selectedEncounter"
  | "selectedEmr"
  | "selectedPatient"
  | "patientData"
  | "loadingStatus"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "PrintMedicalReportPHV"; params: Record<string, unknown> }
  | { message: "GetCheckupReportListPHV"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  userProfile?: Record<string, any>;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof CARD_NAME;

type ActionPayloadMap = {
  [ACTIONS.FETCH]: { encounter?: number };
  [ACTIONS.GET_CHECKUP_LIST]: { encounter?: number };
  [ACTIONS.GET_CHECKUP_REPORT]: { checkupReportId: number; checkupData: HealthRecommendationData };
  [ACTIONS.GET_TEMPLATE]: { name?: string };
  [ACTIONS.CREATE_TEMPLATE]: { data: DoctorRecommendationTemplateSerializer };
  [ACTIONS.EDIT_TEMPLATE]: { templateId: number; data: DoctorRecommendationTemplateSerializer };
  [ACTIONS.DELETE_TEMPLATE]: { templateId: number };
  [ACTIONS.SAVE_REPORT]: { saveAction: ActionReport; data: HealthRecommendationData };
  [ACTIONS.PRINT_REPORT]: { checkupReport: string };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/*================================ Constants ==============================*/

export const StateInitial: State = {
  HealthRecommendationSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["division", {}],
  ["prenameEn", {}],
  ["prenameTh", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, CARD_NAME);

/*================================ Start ==============================*/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const currentEncounter = state.selectedEncounter?.id;
  const currentPatient = state.selectedPatient?.id;

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: true,
    },
  });

  const [encounterList, checkupReportList, doctorNoteTemplateList] = await Promise.all([
    getFilterEncounterPackageList(controller, { patientId: currentPatient }),
    getCheckupReportList(controller, { encounterId: currentEncounter }),
    getDoctorNoteTemplateList(controller, {}),
  ]);

  const initCheckupReportId = checkupReportList.at(0)?.id;

  const checkupReportDetail = initCheckupReportId
    ? await getCheckupReportDetail(controller, { checkupReportId: initCheckupReportId })
    : undefined;

  controller.setState(
    {
      loadingStatus: {
        ...state.loadingStatus,
        [CARD_NAME]: false,
      },
      HealthRecommendationSequence: {
        ...state.HealthRecommendationSequence,
        sequenceIndex: "Action",
        commonOptions: {
          ...state.HealthRecommendationSequence?.commonOptions,
          encounterList,
          checkupReportList,
          doctorNoteTemplateList,
        },
        data: {
          ...state.HealthRecommendationSequence?.data,
          language: "th",
          encounter: currentEncounter,
          checkup_report_data: checkupReportDetail,
        },
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.FETCH })
  );
};

/*================================ Actions ==============================*/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.FETCH]: HandleFetch,
    [ACTIONS.FETCH_DOCTOR_NOTE]: HandleFetchDoctorNotePdf,
    [ACTIONS.FETCH_ALL_PDF]: HandleFetchAllReportPdf,
    [ACTIONS.GET_CHECKUP_LIST]: HandleGetCheckupReportList,
    [ACTIONS.GET_CHECKUP_REPORT]: HandleGetCheckupReport,
    [ACTIONS.GET_TEMPLATE]: HandleGetDoctorNoteTemplateList,
    [ACTIONS.CREATE_TEMPLATE]: HandleCreateDoctorNoteTemplate,
    [ACTIONS.EDIT_TEMPLATE]: HandleEditDoctorNoteTemplate,
    [ACTIONS.DELETE_TEMPLATE]: HandleDeleteDoctorNoteTemplate,
    [ACTIONS.PRINT_REPORT]: HandlePrintCheckupReport,
    [ACTIONS.SAVE_REPORT]: HandleSaveCheckupReport,
  };

  const action = params.action;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

const HandleFetch: Handler<Params<Actions["FETCH"]>> = async (controller, params) => {
  const state = controller.getState();
  const currentEncounter = state.selectedEncounter?.id;
  const currentPatient = state.selectedPatient?.id;
  const masterOptions = state.masterOptions as MasterOptions;
  const data = state.HealthRecommendationSequence?.data as HealthRecommendationData;

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: true,
    },
  });

  const [encounterList, checkupReportList, doctorNoteTemplateList, checkupReportData] =
    await Promise.all([
      getFilterEncounterPackageList(controller, { patientId: currentPatient }),
      getCheckupReportList(controller, { encounterId: currentEncounter }),
      getDoctorNoteTemplateList(controller, {}),
      data?.checkup_report_data?.id
        ? getCheckupReportDetail(controller, { checkupReportId: data.checkup_report_data?.id })
        : undefined,
    ]);

  if (!checkupReportData) {
    controller.setState((prev) => ({
      loadingStatus: {
        ...state.loadingStatus,
        [CARD_NAME]: false,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "Checkup Report Not Found! (ไม่พบสมุดตรวจสุขภาพ)",
      },
      HealthRecommendationSequence: {
        ...state.HealthRecommendationSequence,
        commonOptions: {
          ...prev.HealthRecommendationSequence?.commonOptions,
          encounterList,
          checkupReportList,
          doctorNoteTemplateList,
        },
        data: {
          ...prev.HealthRecommendationSequence?.data,
          checkup_report_data: undefined,
        },
        pdfData: {
          ...prev.HealthRecommendationSequence?.pdfData,
          checkup_report: "",
        },
      },
    }));

    return;
  }

  const defaultPdfBase64 = await createCheckupPdf(controller, { data, masterOptions });

  controller.setState((prev) => ({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: false,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...prev.HealthRecommendationSequence?.commonOptions,
        encounterList,
        checkupReportList,
        doctorNoteTemplateList,
      },
      data: {
        ...prev.HealthRecommendationSequence?.data,
        checkup_report_data: checkupReportData,
      },
      pdfData: {
        ...prev.HealthRecommendationSequence?.pdfData,
        checkup_report: defaultPdfBase64 || "",
      },
    },
  }));
};

const HandleFetchDoctorNotePdf: Handler = async (controller, params) => {
  const state = controller.getState();
  const data = state.HealthRecommendationSequence?.data as HealthRecommendationData;
  const masterOptions = state.masterOptions as MasterOptions;

  const drBase64 = await createDoctorRecommendPdf(controller, { data, masterOptions });

  controller.setState((prev) => ({
    HealthRecommendationSequence: {
      ...prev.HealthRecommendationSequence,
      pdfData: {
        ...prev.HealthRecommendationSequence?.pdfData,
        approve_report: drBase64,
      },
    },
  }));
};

const HandleFetchAllReportPdf: Handler = async (controller, params) => {
  const state = controller.getState();
  const data = state.HealthRecommendationSequence?.data as HealthRecommendationData;
  const masterOptions = state.masterOptions as MasterOptions;

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: true,
    },
  });

  const { checkup_report, approve_report } = await createAllReportPdf(controller, {
    data,
    masterOptions,
  });

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: false,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      pdfData: {
        checkup_report,
        approve_report,
      },
    },
  });
};

const HandleGetCheckupReportList: Handler<Params<Actions["GET_CHECKUP_LIST"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const encounterId = params?.encounter as number;

  const checkupReportList = await getCheckupReportList(controller, { encounterId });

  controller.setState({
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...state.HealthRecommendationSequence?.commonOptions,
        checkupReportList,
      },
    },
  });
};

const HandleGetCheckupReport: Handler<Params<Actions["GET_CHECKUP_REPORT"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const checkupReportId = params.checkupReportId;
  const checkupData = params.checkupData;
  const masterOptions = state.masterOptions as MasterOptions;

  if (!checkupReportId) return;

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: true,
    },
  });

  const checkupReportData = await getCheckupReportDetail(controller, { checkupReportId });

  if (!checkupReportData) return;

  const data = {
    ...checkupData,
    checkup_report_data: checkupReportData,
  };

  const pdfBase64 = await createCheckupPdf(controller, { data, masterOptions });

  controller.setState({
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: false,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      data: {
        ...state.HealthRecommendationSequence?.data,
        ...data,
      },
      pdfData: {
        ...state.HealthRecommendationSequence?.pdfData,
        checkup_report: pdfBase64,
      },
    },
  });
};

const HandleGetDoctorNoteTemplateList: Handler<Params<Actions["GET_TEMPLATE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const name = params.name;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GET_TEMPLATE]: BLC_STATUS.LOADING,
    },
  });

  const doctorNoteTemplateList = await getDoctorNoteTemplateList(controller, { name });

  if (doctorNoteTemplateList.length < 1) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GET_TEMPLATE]: BLC_STATUS.ERROR,
      },
      HealthRecommendationSequence: {
        ...state.HealthRecommendationSequence,
        commonOptions: {
          ...state.HealthRecommendationSequence?.commonOptions,
          doctorNoteTemplateList: [],
        },
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GET_TEMPLATE]: BLC_STATUS.SUCCESS,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...state.HealthRecommendationSequence?.commonOptions,
        doctorNoteTemplateList,
      },
    },
  });
};

const HandleCreateDoctorNoteTemplate: Handler<Params<Actions["CREATE_TEMPLATE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const createData = params.data;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE_TEMPLATE]: BLC_STATUS.LOADING,
    },
  });

  const createResult = await createDoctorNoteTemplate(controller, { createData });

  if (!createResult) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CREATE_TEMPLATE]: BLC_STATUS.ERROR,
      },
    });

    return;
  }

  // fetch doctor note template list
  const doctorNoteTemplateList = await getDoctorNoteTemplateList(controller, {});

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE_TEMPLATE]: BLC_STATUS.SUCCESS,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...state.HealthRecommendationSequence?.commonOptions,
        doctorNoteTemplateList,
      },
    },
  });
};

const HandleEditDoctorNoteTemplate: Handler<Params<Actions["EDIT_TEMPLATE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const templateId = params?.templateId;
  const editData = params.data;
  const btnKey = `${BTN_ACTS.EDIT_TEMPLATE}_${templateId}`;

  if (!templateId) {
    console.warn("[HandleEditDoctorNoteTemplate] Template doesn't exist!");

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.LOADING,
    },
  });

  const editResult = await editDoctorNoteTemplate(controller, { templateId, editData });

  if (!editResult) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [btnKey]: BLC_STATUS.ERROR,
      },
    });

    return;
  }

  // fetch doctor note template list
  const doctorNoteTemplateList = await getDoctorNoteTemplateList(controller, {});

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.SUCCESS,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...state.HealthRecommendationSequence?.commonOptions,
        doctorNoteTemplateList,
      },
    },
  });
};

const HandleDeleteDoctorNoteTemplate: Handler<Params<Actions["DELETE_TEMPLATE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const templateId = params?.templateId;
  const btnKey = `${BTN_ACTS.DELETE_TEMPLATE}_${templateId}`;

  if (!templateId) {
    console.warn("[HandlerDeleteDoctorNoteTemplate] Template doesn't exist!");

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.LOADING,
    },
  });

  const deleteResult = await deleteDoctorNoteTemplate(controller, { templateId });

  if (typeof deleteResult === "number" && deleteResult !== 204) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [btnKey]: BLC_STATUS.ERROR,
      },
    });

    return;
  }

  // fetch doctor note template list
  const doctorNoteTemplateList = await getDoctorNoteTemplateList(controller, {});

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.SUCCESS,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      commonOptions: {
        ...state.HealthRecommendationSequence?.commonOptions,
        doctorNoteTemplateList,
      },
    },
  });
};

const HandlePrintCheckupReport: Handler<Params<Actions["PRINT_REPORT"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const reportState = state.HealthRecommendationSequence?.pdfData?.checkup_report || "";
  const report = params.checkupReport || reportState || "";

  printPdfUrl(report);
};

const HandleSaveCheckupReport: Handler<Params<Actions["SAVE_REPORT"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const saveAction = params.saveAction;
  const data = params?.data as HealthRecommendationData;
  const saveData = data?.checkup_report_data as CheckupReportSerializer;
  const masterOptions = state.masterOptions as MasterOptions;
  const btnKey = `${BTN_ACTS.SAVE_REPORT}_${saveAction}`;

  const errMessage: Record<ActionReport, string> = {
    SAVE: "บันทึกสมุดตรวจสุขภาพไม่สำเร็จ",
    APPROVE: "อนุมัติสมุดตรวจสุขภาพไม่สำเร็จ",
    UNAPPROVE: "ยกเลิกอนุมัติสมุดตรวจสุขภาพไม่สำเร็จ",
  };

  const sucMessage: Record<ActionReport, string> = {
    SAVE: "บันทึกสมุดตรวจสุขภาพสำเร็จ",
    APPROVE: "อนุมัติสมุดตรวจสุขภาพสำเร็จ",
    UNAPPROVE: "ยกเลิกอนุมัติสมุดตรวจสุขภาพสำเร็จ",
  };

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.LOADING,
    },
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: true,
    },
  });

  const saveReportResult = await saveActionCheckupReport(controller, { saveData, saveAction });

  if (!saveReportResult) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [btnKey]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: errMessage[saveAction], // *
      },
      loadingStatus: {
        ...state.loadingStatus,
        [CARD_NAME]: false,
      },
    });

    return;
  }

  // fetch pdf
  const fetchCheckupData: HealthRecommendationData = {
    ...data,
    checkup_report_data: saveReportResult,
  };

  const { checkup_report, approve_report } = await createAllReportPdf(controller, {
    data: fetchCheckupData,
    masterOptions,
  });

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: sucMessage[saveAction],
    },
    loadingStatus: {
      ...state.loadingStatus,
      [CARD_NAME]: false,
    },
    HealthRecommendationSequence: {
      ...state.HealthRecommendationSequence,
      data: {
        ...state.HealthRecommendationSequence?.data,
        checkup_report_data: saveReportResult,
      },
      pdfData: {
        checkup_report,
        approve_report,
      },
    },
  });
};

export const HandleGetCheckupReportListPHV: Handler = async (controller, params) => {
  const emrSummaryData = params.emrSummaryData;
  const encounterId = emrSummaryData?.encounterId;

  const checkupReportList = await getCheckupReportList(controller, { encounterId });

  return checkupReportList;
};

export const HandlePrintMedicalReportPHV: Handler = async (controller, params) => {
  const { emrSummaryData, selectedCheckupReport, language, masterOptions } = params;
  const selectedEncounter = emrSummaryData?.encounterId;
  const selectedLanguage = language || "th";

  const checkupReportData = await getCheckupReportDetail(controller, {
    checkupReportId: selectedCheckupReport,
  });

  const data: HealthRecommendationData = {
    encounter: selectedEncounter,
    language: selectedLanguage,
    checkup_report_data: checkupReportData,
  };

  const { checkup_report } = await createAllReportPdf(controller, { data, masterOptions });

  if (checkup_report) {
    printPdfUrl(checkup_report);
  } else {
    console.warn("[handlePrintMediacalReportPHV] Checkup report doesn't exist!");

    return;
  }
};

/*================================ API Handlers ==============================*/

const getEncounterList: Handler<{ patientId: number }, Promise<EncounterSerializer[]>> = async (
  controller,
  params
) => {
  const patient = params.patientId;

  if (!patient) {
    console.warn("[HealthRecommendation] getEncounterList: Patient doesn't exist!");

    return [];
  }

  const [resp, err] = await EncounterList.list({
    apiToken: controller.apiToken,
    params: {
      patient,
      // active_only: true,
      // unexpired_only: true,
    },
    extra: { division: controller.data?.division },
  });

  if (err) return [];

  return resp.items || [];
};

const getPackageOrderEncounterMapList: Handler<
  { encounterList: (number | undefined)[] },
  Promise<PackageOrderEncounterMapSerializer>
> = async (controller, params) => {
  const encounters = params.encounterList;

  if (!encounters) {
    console.warn("[HealthRecommendation] getPackageOrderEncounterMapList: encounters don't exist!");

    return {};
  }

  const [resp, err] = await PackageOrderEncounterMapList.get({
    apiToken: controller.apiToken,
    params: {
      encounters,
    },
    extra: { division: controller.data?.division },
  });

  if (err) return {};

  return resp;
};

const getFilterEncounterPackageList: Handler<
  { patientId: number },
  Promise<EncounterSerializer[]>
> = async (controller, params) => {
  const patientId = params.patientId;

  if (!patientId) {
    console.warn("[HealthRecommendation] getFilterEncounterPackageList: Patient doesn't exist!");
    return [];
  }

  const encounterList = await getEncounterList(controller, { patientId });

  if (!encounterList || encounterList.length < 1) {
    console.warn("[HealthRecommendation] getFilterEncounterPackageList: encounterList is empty!");
    return [];
  }

  const encounterIds = encounterList.map((item) => item.id).filter(Boolean);

  if (encounterIds.length < 1) {
    console.warn("[HealthRecommendation] getFilterEncounterPackageList: encounterIds is empty!");
    return [];
  }

  const packageEncounterList = await getPackageOrderEncounterMapList(controller, {
    encounterList: encounterIds,
  });

  if (!packageEncounterList || Object.keys(packageEncounterList).length < 1) {
    console.warn(
      "[HealthRecommendation] getFilterEncounterPackageList: packageEncounterList is empty!"
    );
    return [];
  }

  const encounterIdsWithPackages = Object.entries(packageEncounterList)
    .filter(([_, value]) => Array.isArray(value.package_orders) && value.package_orders.length > 0)
    .map(([key]) => Number(key));

  const filteredEncounterList = encounterList.filter((encounter) =>
    encounterIdsWithPackages.includes(encounter.id as number)
  );

  return filteredEncounterList;
};

const getCheckupReportList: Handler<
  { encounterId: number },
  Promise<CheckupReportSerializer[]>
> = async (controller, params) => {
  const encounter = params.encounterId;

  if (!encounter) {
    console.warn("[HealthRecommendation] getCheckupReportList: encounter doesn't exist!");

    return [];
  }

  const [resp, err] = await CheckupReportList.list({
    apiToken: controller.apiToken,
    params: { encounter },
    extra: { division: controller.data?.division },
  });

  if (err) return [];

  return resp.items || [];
};

const getCheckupReportDetail: Handler<
  { checkupReportId: number },
  Promise<CheckupReportSerializer | undefined>
> = async (controller, params) => {
  const pk = params.checkupReportId;

  if (!pk) {
    console.warn("[HealthRecommendation] getCheckupReportDetail: Checkup Report doesn't exist!");

    return undefined;
  }

  const [resp, err] = await CheckupReportDetail.retrieve({
    apiToken: controller.apiToken,
    pk,
    extra: { division: controller.data?.division },
  });

  if (err) return undefined;

  return resp;
};

const getDoctorNoteTemplateList: Handler<
  { name?: string },
  Promise<DoctorRecommendationTemplateSerializer[]>
> = async (controller, params) => {
  const name = params.name;

  const [resp, err] = await DoctorRecommendationTemplateList.list({
    apiToken: controller.apiToken,
    params: { ...(name ? { name } : {}) },
    extra: { division: controller.data?.division },
  });

  if (err) return [];

  return resp.items || [];
};

const saveActionCheckupReport: Handler<
  { saveData: CheckupReportSerializer; saveAction: ActionReport },
  Promise<CheckupReportSerializer | null>
> = async (controller, params) => {
  const { saveData, saveAction } = params;

  let defaultData: Partial<CheckupReportSerializer> = {
    doctor_recommendation: saveData.doctor_recommendation,
  };

  if (saveAction === "APPROVE") defaultData.approve = true;
  else if (saveAction === "UNAPPROVE") defaultData.approve = false;
  else if (saveAction === "SAVE") delete defaultData.approve;

  const [resp, err] = await CheckupReportDetail.update({
    apiToken: controller.apiToken,
    pk: saveData?.id,
    data: defaultData,
    extra: { division: controller.data?.division },
  });

  if (err) return null;

  return resp;
};

const createDoctorNoteTemplate: Handler<
  { createData: DoctorRecommendationTemplateSerializer },
  Promise<DoctorRecommendationTemplateSerializer | null>
> = async (controller, params) => {
  const data = params.createData;

  const [resp, err] = await DoctorRecommendationTemplateList.create({
    apiToken: controller.apiToken,
    data,
    extra: { division: controller.data?.division },
  });

  if (err) return null;

  return resp;
};

const editDoctorNoteTemplate: Handler<
  { templateId: number; editData: DoctorRecommendationTemplateSerializer },
  Promise<DoctorRecommendationTemplateSerializer | null>
> = async (controller, params) => {
  const pk = params.templateId;
  const data = params.editData;

  const [resp, err] = await DoctorRecommendationTemplateDetail.update({
    apiToken: controller.apiToken,
    pk,
    data,
    extra: { division: controller.data?.division },
  });

  if (err) return null;

  return resp;
};

const deleteDoctorNoteTemplate: Handler<
  { templateId: number },
  Promise<number | null> // return http status code
> = async (controller, params) => {
  const pk = params.templateId;

  const [resp, err, net] = await DoctorRecommendationTemplateDetail.delete({
    apiToken: controller.apiToken,
    pk,
    extra: { division: controller.data?.division },
  });

  if (err) return null;

  return net.status; // 204 - No Content
};

/*================================ PDF Handlers ==============================*/

const getBasePdfContext: Handler<
  {
    data: HealthRecommendationData;
  },
  Promise<{
    reportData?: CheckupReportSerializer;
    language: Language;
    printer: string;
    reportParams?: ReportParams;
  }>
> = async (controller, params) => {
  const data = params.data;
  const printer = controller.data.userProfile?.full_name || "";
  const reportData = data?.checkup_report_data;
  const language = data?.language || "th";
  const reportParams = {
    checkupReportId: reportData?.id,
    language,
  };

  return { reportData, language, printer, reportParams };
};

const getUserProfileData: Handler<
  { language: Language; masterOptions: MasterOptions },
  Promise<{ doctor: string; doctor_number: string }>
> = async (controller, params) => {
  const language = params?.language || "th";
  const isEng = language === "en";
  const masterOptions = params.masterOptions;
  const userProfile = controller.data?.userProfile;

  const prenameEn = findPrename(masterOptions?.prenameEn || [], userProfile?.pre_name_en);
  const prenameTh = findPrename(masterOptions?.prenameTh || [], userProfile?.pre_name);

  const fullNameEn = `${prenameEn} ${userProfile?.first_name_en || ""} ${
    userProfile?.last_name_en || ""
  }`;
  const fullNameTh = `${prenameTh} ${userProfile?.first_name || ""} ${
    userProfile?.last_name || ""
  }`;

  return {
    doctor: isEng ? fullNameEn : fullNameTh,
    doctor_number: userProfile?.licence || "",
  };
};

const getCheckupReportCover: Handler<
  { reportParams?: ReportParams },
  Promise<CheckupReportCover>
> = async (controller, params) => {
  const checkupReportId = params.reportParams?.checkupReportId;
  const language = params.reportParams?.language;

  if (!checkupReportId) return {};

  const [resp, err] = await CheckupReportCoverExaminationPrint.get({
    apiToken: controller.apiToken,
    pk: checkupReportId,
    params: { language },
    extra: { division: controller.data?.division },
  });

  if (err) return {};

  const allergies = resp?.physical_examination?.allergies?.items;

  const allergiesData = Object.entries(
    allergies.reduce((acc: any, { label, name }: any) => {
      if (!acc[label]) acc[label] = new Set();
      acc[label].add(name);
      return acc;
    }, {})
  ).map(([label, names]) => ({
    label,
    name: Array.from(names as string).join(", "),
  }));

  let patient_name = resp?.cover?.patient_name || "";

  if (language === "en") {
    const [patientRes, patientErr, patientNet] = await PatientDetailViewByHn.get({
      apiToken: controller.apiToken,
      hn: resp?.cover?.patient_hn,
    });

    patient_name = patientRes?.full_name_en;
  }

  return {
    ...resp,
    cover: {
      ...resp.cover,
      patient_name,
    },
    physical_examination: {
      ...resp?.physical_examination,
      allergies: {
        items: allergiesData,
      },
    },
  };
};

const getCheckupReportLab: Handler<
  { reportParams?: ReportParams },
  Promise<CheckupReportLab>
> = async (controller, params) => {
  const checkupReportId = params.reportParams?.checkupReportId;
  const language = params.reportParams?.language;

  if (!checkupReportId) return {};

  const [resp, err] = await CheckupReportLabPrint.get({
    apiToken: controller.apiToken,
    pk: checkupReportId,
    params: { language },
    extra: { division: controller.data?.division },
  });

  if (err) return {};

  return resp;
};

const getCheckupReportXray: Handler<
  { reportParams?: ReportParams },
  Promise<CheckupReportXray>
> = async (controller, params) => {
  const checkupReportId = params.reportParams?.checkupReportId;
  const language = params.reportParams?.language;

  if (!checkupReportId) return {};

  const [resp, err] = await CheckupReportXrayPrint.get({
    apiToken: controller.apiToken,
    pk: checkupReportId,
    params: { language },
    extra: { division: controller.data?.division },
  });

  if (err) return {};

  return resp;
};

const createCheckupPdf: Handler<
  { data: HealthRecommendationData; masterOptions: MasterOptions },
  Promise<string>
> = async (controller, params) => {
  const data = params.data;
  const masterOptions = params.masterOptions;
  const language = data?.language as Language;
  const { reportData, printer, reportParams } = await getBasePdfContext(controller, { data });

  const checkupReportDetail = reportData || data?.checkup_report_data;
  const isApprove = (checkupReportDetail && checkupReportDetail?.approved === true) || false;

  const [coverData, labData, xrayData] = await Promise.all([
    getCheckupReportCover(controller, { reportParams }),
    getCheckupReportLab(controller, { reportParams }),
    getCheckupReportXray(controller, { reportParams }),
  ]);

  const checkupDocDef = await FormCheckupReport({
    coverData,
    labData,
    xrayData,
    data: reportData,
    printer: printer,
    language,
  });

  const checkupBase64 = await createPdfBase64(checkupDocDef);

  if (isApprove) {
    const drBase64 = await createDoctorRecommendPdf(controller, { data, masterOptions });

    return await mergeBase64Pdfs([checkupBase64, drBase64]);
  }

  return checkupBase64;
};

const createDoctorRecommendPdf: Handler<
  { data: HealthRecommendationData; masterOptions: MasterOptions },
  Promise<string>
> = async (controller, params) => {
  const data = params.data;
  const masterOptions = params.masterOptions;
  const { language, printer, reportParams } = await getBasePdfContext(controller, { data });

  const doctorNoteArray = await getDoctorNoteArray(data.checkup_report_data);
  const doctorApproveData = await getUserProfileData(controller, { language, masterOptions });
  const doctorNoteData: CheckupReportDoctorNote = {
    ...doctorApproveData,
    doctorNoteArray,
  };

  const coverData = await getCheckupReportCover(controller, { reportParams });

  const drDocDef = await FormDoctorRecommendationReport({
    coverData,
    doctorNoteData,
    language,
    printer,
  });

  return await createPdfBase64(drDocDef);
};

const createAllReportPdf: Handler<
  { data: HealthRecommendationData; masterOptions: MasterOptions },
  Promise<PdfData>
> = async (controller, params) => {
  const data = params.data;
  const masterOptions = params.masterOptions;

  const { reportData, language, printer, reportParams } = await getBasePdfContext(controller, {
    data,
  });
  const isApprove = (reportData && reportData?.approved === true) || false;
  const doctorNoteArray = await getDoctorNoteArray(reportData);

  const [coverData, labData, xrayData, doctorApproveData] = await Promise.all([
    getCheckupReportCover(controller, { reportParams }),
    getCheckupReportLab(controller, { reportParams }),
    getCheckupReportXray(controller, { reportParams }),
    getUserProfileData(controller, { language, masterOptions }),
  ]);

  const doctorNoteData: CheckupReportDoctorNote = {
    ...doctorApproveData,
    doctorNoteArray,
  };

  // --- Checkup PDF
  const checkupDocDef = await FormCheckupReport({
    coverData,
    labData,
    xrayData,
    data: reportData,
    printer,
    language,
  });

  const checkupBase64 = await createPdfBase64(checkupDocDef);

  // --- Doctor Recommendation PDF
  const drDocDef = await FormDoctorRecommendationReport({
    coverData,
    doctorNoteData,
    language,
    printer,
  });

  const doctorRecBase64 = await createPdfBase64(drDocDef);

  if (isApprove) {
    const mergedBase64 = await mergeBase64Pdfs([checkupBase64, doctorRecBase64]);

    return {
      checkup_report: mergedBase64,
      approve_report: doctorRecBase64,
    };
  }

  return {
    checkup_report: checkupBase64,
    approve_report: doctorRecBase64,
  };
};

/*================================ Utils ==============================*/

const findPrename = (options: OptionType[], value: number | undefined) =>
  options.find((item) => item.value === value)?.text || "";

const getDoctorNoteArray = async (checkupReportData?: CheckupReportSerializer) => {
  const htmlToPdfmake = await getHtmlToPdfmake();
  const doctorNote = htmlToPdfmake.default(checkupReportData?.doctor_recommendation || "-");

  return Array.isArray(doctorNote) ? doctorNote : [doctorNote];
};

const createPdfBase64 = async (docDef: ITDocumentDefinitions) => {
  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  return new Promise<string>((resolve) => pdf.getBase64(resolve));
};

const mergeBase64Pdfs = async (pdfList: string[]): Promise<string> => {
  if (!pdfList || pdfList.length < 1) {
    throw new Error("No PDF Base64 Provided!");
  }

  const cleanBase64 = (pdfBase64: string) =>
    pdfBase64.replace(/^data:application\/pdf;base64,/, "");

  const mergedPdf = await PDFDocument.create();

  for (const rawBase64 of pdfList) {
    if (!rawBase64) continue;

    const pdfBase64 = cleanBase64(rawBase64);
    const pdfBytes = base64ToUint8Array(pdfBase64);
    const pdfDoc = await PDFDocument.load(pdfBytes);

    const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
    pages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedBytes = await mergedPdf.save();

  const mergedBase64 = btoa(
    Array.from(mergedBytes)
      .map((byte) => String.fromCharCode(byte))
      .join("")
  );

  return mergedBase64;
};

const printPdfUrl = (pdfBase64: string) => {
  if (!pdfBase64) return;

  const byteCharacters = atob(pdfBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });

  const blobUrl = URL.createObjectURL(blob);

  printURL(blobUrl);
};
